import { deepObjectClone } from '@utils/';

// Functions for handling folder structure
/**
 * processFolderItem - Processes a folder item and collects its name if it's a folder.
 *
 * @param {Object} item - The folder item to process.
 * @param {string[]} listOfFolderNames - The list to collect folder names.
 */
export const processFolderItem = (item, listOfFolderNames) => {
  if (item.isFolder) {
    listOfFolderNames.push(item.folder_name);
  }
  if (item.children && item.children.length > 0) {
    for (const child of item.children) {
      processFolderItem(child, listOfFolderNames);
    }
  }

  return listOfFolderNames;
};

/**
 * extractFolderNames - Traverses folders recursively and returns an array of folder names.
 *
 * @returns {string[]} - Array of folder names.
 */

export const extractFolderNames = (modelTree) => {
  const listOfFolderNames = [];

  for (const folder of modelTree) {
    processFolderItem(folder, listOfFolderNames);
  }

  return listOfFolderNames;
};

/**
 * generateFolderHierarchy - builds a tree structure from a list of folders.
 *
 * @returns {Array} - Array of folder objects with nested children.
 */

export const generateFolderHierarchy = (currentModelTree) => {
  // Recursive function to traverse the folder structure
  function traverse(modelTree) {
    return modelTree.reduce((acc, item) => {
      if (item.isFolder) {
        const folderObject = {
          name: item.folder_name,
          id: item.id,
          company: item.company,
          children:
            item.children && item.children.length > 0
              ? traverse(item.children)
              : [],
        };

        acc.push(folderObject);
      }
      return acc;
    }, []);
  }

  // Start the traversal with the root folders array
  return traverse(currentModelTree);
};

/**
 *
 * @param {*} modelTree
 * @param {*} currentPath
 * @param {*} applyFunction
 * @param  {...any} args
 * @returns
 */

export const traverseAndUpdate = (modelTree, path, applyFunction, ...args) => {
  if (!modelTree.length) {
    return modelTree;
  }

  const newTree = deepObjectClone(modelTree);
  let currentLevel = newTree;
  let targetNode;

  if (path.length === 1) {
    // Directly apply function to the root level
    return applyFunction(newTree, ...args);
  }

  for (let i = 1; i < path.length; i++) {
    const parentFolderId = path[i].id;
    targetNode = currentLevel.find((item) => item.id === parentFolderId);

    if (!targetNode) break;

    if (i < path.length - 1) {
      currentLevel = targetNode.children;
    } else {
      targetNode.children = applyFunction(targetNode.children, ...args);
      break;
    }
  }

  return newTree;
};

export const findParent = (modelTree, targetFolderId, basePath) => {
  const finedFolderAndPath = traverseAndProcessTree(modelTree, {
    folderInfoId: targetFolderId,
  });

  const folderStack = [basePath[0], ...finedFolderAndPath.path];

  return folderStack;
};

export const applySearchFilter = (item, searchValue) => {
  return (
    (item.file_name &&
      item.file_name.toLowerCase().includes(searchValue.toLowerCase())) ||
    (item.folder_name &&
      item.folder_name.toLowerCase().includes(searchValue.toLowerCase()))
  );
};

export const deleteTargetModel = (items, targetModels) => {
  return items.filter((item) => item.id !== targetModels.id);
};

export const deleteTargetModels = (items, targetModels) => {
  return items.filter((item) => {
    return targetModels.every((targetItem) => item.id !== targetItem.id);
  });
};

export const deleteTargetItems = (items, targetModels, targetFolders) => {
  let filteredItems = items;
  if (targetFolders.length !== 0)
    filteredItems = deleteTargetFolders(filteredItems, targetFolders);

  if (targetModels.length !== 0)
    filteredItems = deleteTargetModels(filteredItems, targetModels);

  return filteredItems;
};

export const updateFolderName = (items, targetFolder, newFolderName) => {
  return items.map((item) =>
    item.id === targetFolder.id ? { ...item, folder_name: newFolderName } : item
  );
};

export const updateItemFavorite = (items, targetItem) => {
  // items -> array of objects of folders and models
  return items.map((item) =>
    // toggle favourite status for item
    item.id === targetItem.id ? { ...item, favourite: !item.favourite } : item
  );
};

export const updateModelStatus = (models, targetModel) => {
  return models.map((model) =>
    model.id === targetModel.id ? { ...model, status: 'Submitted' } : model
  );
};

export const deleteTargetFolder = (items, targetFolder) => {
  // Find and reparent children of the deleted folder
  const targetFolderChildren = items.find(
    (item) => item.id === targetFolder.id
  ).children;
  targetFolderChildren.forEach(
    (item) => (item.parentId = targetFolder.parentId)
  );

  // Remove deleted folder and add its children to the current level
  const currentItemsArr = deleteTargetModel(items, targetFolder);

  return [...currentItemsArr, ...targetFolderChildren];
};

export const deleteTargetFolders = (items, targetFolders) => {
  // Find all children of target folders
  const foundFolders = items.filter((item) =>
    targetFolders.some((targetFolder) => {
      return item.id === targetFolder.id;
    })
  );

  const foundFoldersChildren = foundFolders
    .map((folder) => folder.children)
    .flat();

  // Assuming ALL target folders have the same parent, reparent all items
  const parentId = targetFolders[0].parentId;
  foundFoldersChildren.forEach((item) => (item.parentId = parentId));

  const currentItemsArr = deleteTargetModels(items, targetFolders);

  return [...currentItemsArr, ...foundFoldersChildren];
};

// Functions for handling sorting

export const sortModelTreeByOrder = (modelTree, order = 'asc') => {
  const newModelTree = deepObjectClone(modelTree);

  return newModelTree.sort((a, b) => {
    const nameA = a.isFolder
      ? a.folder_name.toLowerCase()
      : a.file_name.toLowerCase();
    const nameB = b.isFolder
      ? b.folder_name.toLowerCase()
      : b.file_name.toLowerCase();

    if (nameA < nameB) return order === 'asc' ? -1 : 1;
    if (nameA > nameB) return order === 'asc' ? 1 : -1;
    return 0;
  });
};

export const sortByFoldersFirstFilesSecond = (modelTree) => {
  const newModelTree = deepObjectClone(modelTree);

  return newModelTree.sort((a, b) => {
    if (a.isFolder && !b.isFolder) return -1;
    if (!a.isFolder && b.isFolder) return 1;
    return 0;
  });
};

export const sortFunctions = {
  file_name: (order) => (a, b) => {
    const nameA = a.isFolder
      ? a.folder_name.toLowerCase()
      : a.file_name.toLowerCase();
    const nameB = b.isFolder
      ? b.folder_name.toLowerCase()
      : b.file_name.toLowerCase();

    if (nameA < nameB) return order === 'asc' ? -1 : 1;
    if (nameA > nameB) return order === 'asc' ? 1 : -1;
    return 0;
  },
  last_modified: (order) => (a, b) =>
    (new Date(a.last_modified) - new Date(b.last_modified)) *
    (order === 'asc' ? 1 : -1),
  status: (order) => (a, b) =>
    a.status?.toLowerCase().localeCompare(b.status?.toLowerCase()) *
    (order === 'asc' ? 1 : -1),
  owner: (order) => (a, b) =>
    a.owner?.toLowerCase().localeCompare(b.owner?.toLowerCase()) *
    (order === 'asc' ? 1 : -1),
};

// export const sortedByFavourites = (models) => {
//   return models.sort((a, b) => b.favourite - a.favourite);
// };

export const modelsSorted = (models, sortConfig) => {
  const sortFunction = sortFunctions[sortConfig.key];

  if (!sortFunction) return sortByFoldersFirstFilesSecond(models);

  const sortedModels = [...models].sort(sortFunction(sortConfig.direction));
  return sortByFoldersFirstFilesSecond(sortedModels);
};
export const filterAndSortModels = (
  currentTree,
  searchValue,
  sortConfig,
  showFavorites
) => {
  let filteredModels = currentTree;

  if (showFavorites) {
    filteredModels = traverseAndProcessTree(filteredModels, {
      isFavoriteFilter: true,
    });
  }

  filteredModels = traverseAndProcessTree(filteredModels, { searchValue });
  return modelsSorted(filteredModels, sortConfig);
};

export const traverseAndProcessTree = (
  modelTree,
  {
    searchValue = null,
    isFavoriteFilter = false,
    folderInfoId = null,
    insertItem = null,
    targetFolder = null,
  } = {}
) => {
  if (!modelTree.length) {
    return searchValue || isFavoriteFilter || folderInfoId ? modelTree : [];
  }

  let newModelTree = [];
  let stack = modelTree.map((node) => ({
    node,
    path: [{ name: node.folder_name, id: node.id }],
  }));

  let visited = new Set();

  while (stack.length) {
    const { node, path } = stack.pop();

    // Search by folder ID
    if (folderInfoId && node.id === folderInfoId) {
      return { folders: node, path };
    }

    // Inserting an item into the desired folder
    if (targetFolder && node.id === targetFolder.id) {
      node.children.push({ ...insertItem, parentId: targetFolder.id });
      newModelTree = [...modelTree];
      break;
    } else if (targetFolder && targetFolder.id === 0) {
      return [...modelTree, { ...insertItem, parentId: null }];
    }

    if (!visited.has(node.id)) {
      // Filtering by favourites
      if (isFavoriteFilter && node.favourite) {
        newModelTree.push(node);
      }

      // Filtering by search value
      if (searchValue && applySearchFilter(node, searchValue)) {
        newModelTree.push(node);
      }

      visited.add(node.id);
    }

    if (node.children) {
      node.children.forEach((child) =>
        stack.push({
          node: child,
          path: [...path, { name: child.folder_name, id: child.id }],
        })
      );
    }
  }

  // Return the result for different cases
  if (folderInfoId || folderInfoId === 0) {
    return { folders: null, path: [] }; // If the item is not found by id
  }

  return searchValue || isFavoriteFilter ? newModelTree : modelTree;
};

export const currentLevelDisplay = (modelTree, folderStack) => {
  let fileSystem = modelTree;
  for (let index = 0; index < folderStack.length; index++) {
    const currentLevelParent = folderStack[index];
    if (index > 0) {
      const foundParent = fileSystem.find(
        (node) => node.id === currentLevelParent.id
      );
      fileSystem = foundParent.children;
    }
  }
  return fileSystem;
};

// Functions for handling user

export function getUserTimeZone() {
  // try to get timezone safely
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return 'America/New_York';
  }
}

export function getUserLocale() {
  // try to get locale safely
  const defaultLocale = 'en-CA';
  try {
    return navigator.language || navigator.userLanguage || defaultLocale;
  } catch (error) {
    // navigator not available
    return defaultLocale;
  }
}

// Functions for handling notes

export const insertCurrentTimeStamp = (textAreaRef, notes) => {
  const currentTime = new Date().toLocaleString('en-GB', {
    timeZone: getUserTimeZone(),
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const textarea = textAreaRef.current;
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;

  const timestamp = `${currentTime} - `;
  const newText = `${notes.slice(0, start)}${timestamp}${notes.slice(end)}`;

  setTimeout(() => {
    textarea.selectionStart = textarea.selectionEnd = start + timestamp.length;
    textarea.focus();
  }, 0);

  return newText;
};
